import type { ThemeTokens } from './types';
import { TOKEN_MAP } from './tokenMap';

const GOOGLE_FONTS_LINK_ID = 'google-fonts-theme';

/**
 * Applies a ThemeTokens object to the document root via inline CSS custom
 * properties. Inline styles on :root take precedence over the static
 * [data-theme='dark'] CSS block, so this works for all theme combinations.
 *
 * The data-theme attribute is still set so component-level dark selectors
 * (e.g. [data-theme='dark'] .icon) continue to fire.
 */
export function applyTheme(theme: ThemeTokens): void {
  const root = document.documentElement;

  // Write every token as an inline style on :root
  for (const [field, cssVar] of Object.entries(TOKEN_MAP) as [string, string][]) {
    const value = (theme as unknown as Record<string, string>)[field];
    if (value !== undefined) {
      root.style.setProperty(cssVar, value);
    }
  }

  // Keep data-theme in sync for component-level CSS selectors
  root.setAttribute('data-theme', theme.colorMode);

  // Manage the Google Fonts <link> tag
  let link = document.getElementById(GOOGLE_FONTS_LINK_ID) as HTMLLinkElement | null;
  if (theme.googleFontsUrl) {
    if (!link) {
      link = document.createElement('link');
      link.id = GOOGLE_FONTS_LINK_ID;
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
    if (link.href !== theme.googleFontsUrl) {
      link.href = theme.googleFontsUrl;
    }
  } else {
    link?.remove();
  }
}
